import React from 'react';
import {
  Button,
  InputAdornment,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import esLocale from 'date-fns/locale/es';
import SearchIcon from '@material-ui/icons/Search';
import FilterListIcon from '@material-ui/icons/FilterList';
import { EMPTY_FILTERS, SEARCH_KEY_LABELS } from '../constants';
import type { AssistedAffiliationFilterBarProps, SearchKey } from '../types';
import useStyle from '../styles';

// Sin estado propio: la consulta es de la pantalla, así que aquí solo se reportan los cambios.
// El rango son dos `KeyboardDatePicker` porque la app no tiene un date range.
const AssistedAffiliationFilterBar = ({
  filters,
  onFiltersChange,
}: AssistedAffiliationFilterBarProps) => {
  const classes = useStyle();
  const { searchKey, searchTerm, dateFrom, dateTo } = filters;

  const updateFilter = <K extends keyof typeof filters>(
    key: K,
    value: typeof filters[K],
  ) => onFiltersChange({ ...filters, [key]: value });

  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
      <div className={classes.filterBar}>
        <TextField
          select
          label="Buscar por"
          value={searchKey}
          onChange={(event) => updateFilter('searchKey', event.target.value as SearchKey)}
          className={classes.filterField}
          variant="outlined"
          size="small"
        >
          {Object.entries(SEARCH_KEY_LABELS).map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(event) => updateFilter('searchTerm', event.target.value)}
          className={classes.searchField}
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <KeyboardDatePicker
          className={classes.dateField}
          label="Desde"
          value={dateFrom}
          onChange={(date) => updateFilter('dateFrom', date)}
          maxDate={dateTo || undefined}
          format="dd/MM/yyyy"
          placeholder="dd/mm/aaaa"
          variant="inline"
          inputVariant="outlined"
          size="small"
          autoOk
          disableFuture
          invalidDateMessage="Fecha inválida"
          maxDateMessage="No puede ser posterior a la fecha final"
          KeyboardButtonProps={{ 'aria-label': 'fecha-desde' }}
        />

        <KeyboardDatePicker
          className={classes.dateField}
          label="Hasta"
          value={dateTo}
          onChange={(date) => updateFilter('dateTo', date)}
          minDate={dateFrom || undefined}
          format="dd/MM/yyyy"
          placeholder="dd/mm/aaaa"
          variant="inline"
          inputVariant="outlined"
          size="small"
          autoOk
          disableFuture
          invalidDateMessage="Fecha inválida"
          minDateMessage="No puede ser anterior a la fecha inicial"
          KeyboardButtonProps={{ 'aria-label': 'fecha-hasta' }}
        />

        <Button
          className={classes.clearFilters}
          onClick={() => onFiltersChange(EMPTY_FILTERS)}
          startIcon={<FilterListIcon />}
        >
          Limpiar filtros
        </Button>
      </div>
    </MuiPickersUtilsProvider>
  );
};

export default AssistedAffiliationFilterBar;
